import React, { useMemo } from 'react';

import { getA11ySelector, useA11ySelector } from '@sg/a11y-helpers';
import { type useScrollspy } from '@sg/garnish';
import { ModifierGroupVariation } from '@sg/graphql-schema';
import {
  CustomizationIngredientsGrid,
  type CustomizationMachineActorRef,
  type CustomizationMachineTypes,
  CustomizationModificationItem,
  useCustomizationContext,
  useCustomizationHelpers,
} from '@sg/shared-fe/features/customization';
import { useDietaryRestrictions } from '@sg/shared-fe/features/menu';

import { useDietaryPreferencesMachine } from '../../../../../../../../context';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationModificationGroup = (props: Props) => {
  const {
    actor,
    modificationGroup,
    activeStepperId,
    formattedQuantity,
    helperText,
    scrollSpy,
    resetStepperTimeout,
    clearStepperTimeout,
    setActiveStepperId,
    onAddIngredient,
    onRemoveIngredient,
  } = props;

  // ─── a11y Selectors ────────────────────────────────────────────────────────

  const { props: a11yProps } = useA11ySelector('customization.modification-group.container', {
    group: modificationGroup.displayName.toLowerCase(),
  });

  // ─────────────────────────────────────────────────────────────────────

  const { isAllergensEnabled, restrictions } = useDietaryPreferencesMachine({
    referrer: 'Customization',
  });
  const getRestrictionNotice = useDietaryRestrictions({ isAllergensEnabled, restrictions }); // prettier-ignore

  // ─── Context ─────────────────────────────────────────────────────────

  const {
    addIngredient,
    removeIngredient,
    toggleSplitPortion,
    sortModifications,
    checkIfCanAddMoreModifications,
    getMaxPortionsForGroup,
    getUpsellPriceForIngredient,
    getPortionOptionsForIngredient,
  } = useCustomizationHelpers({ actor, onAddIngredient, onRemoveIngredient });

  const {
    allModificationsMap,
    modifications,
    activeModificationsMap,
    initialModificationsMap,
    hasReachedMaxModifiersCounts,
    isUpsellPricingEnabled,
  } = useCustomizationContext({ actor });

  // ─── Derived Data ─────────────────────────────────────────────────────

  const sortedModifications = useMemo(
    () => sortModifications(initialModificationsMap, modificationGroup),
    [sortModifications, initialModificationsMap, modificationGroup],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (!allModificationsMap || !modifications) {
    return null;
  }

  return (
    <CustomizationIngredientsGrid.Section
      key={modificationGroup.id}
      sectionId={modificationGroup.id}
      ingredientKindName={modificationGroup.displayName}
      measureCoordinates={scrollSpy.measureCoordinates}
      deregisterTarget={scrollSpy.deregisterTarget}
      helperText={helperText}
      subheading={formattedQuantity}
      {...a11yProps}
    >
      <CustomizationIngredientsGrid.SectionStack>
        {sortedModifications.map((modification, index) => {
          const ingredientId = modification.ingredient?.id;
          const modificationEntry = ingredientId ? allModificationsMap?.get(ingredientId) : null; // prettier-ignore

          if (!modificationEntry) return null;

          const { modificationGroupName } = modificationEntry;

          const allergenProperties = modification.ingredient?.allergenProperties ?? []; // prettier-ignore
          const dietaryProperties = modification.ingredient?.dietaryProperties ?? []; // prettier-ignore
          const restrictionNotice = getRestrictionNotice({ allergenProperties, dietaryProperties }); // prettier-ignore

          const canAddMoreWithinGroup = checkIfCanAddMoreModifications(modificationGroupName); // prettier-ignore
          const canAddMore = canAddMoreWithinGroup && !hasReachedMaxModifiersCounts; // prettier-ignore
          const maxPortionsForGroup = getMaxPortionsForGroup(modificationGroupName); // prettier-ignore
          const isSplitPortion = modificationGroup.variation === ModifierGroupVariation.SplitPortion; // prettier-ignore
          const upsellPrice = getUpsellPriceForIngredient(ingredientId);
          const portionOptions = getPortionOptionsForIngredient(ingredientId);

          const a11yProps = getA11ySelector('customization.modification-group.item', {
            group: modificationGroupName.toLowerCase(),
            index,
          });

          return (
            <CustomizationModificationItem
              key={modification.id}
              activeModificationsMap={activeModificationsMap}
              activeStepperId={activeStepperId}
              canAddMore={canAddMore}
              maxPortionsForGroup={maxPortionsForGroup}
              modification={modification}
              notice={restrictionNotice}
              upsellPrice={upsellPrice}
              portionOptions={portionOptions}
              isUpsellPricingEnabled={isUpsellPricingEnabled}
              onIncrement={addIngredient}
              onDecrement={removeIngredient}
              onToggleSplitPortion={toggleSplitPortion}
              isSplitPortion={isSplitPortion}
              resetStepperTimeout={resetStepperTimeout}
              clearStepperTimeout={clearStepperTimeout}
              setActiveStepperId={setActiveStepperId}
              {...a11yProps.props}
            />
          );
        })}
      </CustomizationIngredientsGrid.SectionStack>
    </CustomizationIngredientsGrid.Section>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: CustomizationMachineActorRef;
  modificationGroup: CustomizationMachineTypes.ProductModifierGroup;
  activeStepperId: string | null;
  helperText?: string | null;
  formattedQuantity?: string;
  scrollSpy: ReturnType<typeof useScrollspy>;
  onAddIngredient?: (ingredientId: string) => void;
  onRemoveIngredient?: (ingredientId: string) => void;
  resetStepperTimeout: () => void;
  clearStepperTimeout: () => void;
  setActiveStepperId: (id: string | null) => void;
};
